/**
 * Per-job offline readiness indicator (status only, no manual download button).
 * Shows: "Offline ready ✅", "Preparing offline…", or "Not yet cached"
 */

import { useState, useEffect } from "react";
import { getSnapshot, getSnapshotStatus, type SnapshotStatus } from "@/lib/jobSnapshotManager";
import { onPrefetchChange, type PrefetchProgress } from "@/lib/autoPrefetch";
import { isNative } from "@/lib/capacitor";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  jobId: string;
  compact?: boolean;
}

export function JobOfflineStatus({ jobId, compact = false }: Props) {
  const [status, setStatus] = useState<SnapshotStatus>("not_available");
  const [preparing, setPreparing] = useState(false);

  useEffect(() => {
    if (!isNative) return;

    // Initial check
    getSnapshot(jobId).then(snap => {
      setStatus(getSnapshotStatus(snap));
    }).catch(() => { /* status stays as-is; getSnapshot reports internally */ });

    // Listen for prefetch progress — update if our job is being cached
    const unsub = onPrefetchChange((_s, progress: PrefetchProgress) => {
      if (progress.currentJobId === jobId) {
        setPreparing(true);
      } else if (preparing) {
        // Re-check if we just finished
        getSnapshot(jobId).then(snap => {
          setStatus(getSnapshotStatus(snap));
          setPreparing(false);
        }).catch(() => { setPreparing(false); });
      }
    });

    return unsub;
  }, [jobId, preparing]);

  if (!isNative) return null;

  const icon = preparing ? (
    <Loader2 className={cn("animate-spin", compact ? "w-3 h-3" : "w-3.5 h-3.5")} />
  ) : status === "available" ? (
    <CheckCircle2 className={cn("text-emerald-600", compact ? "w-3 h-3" : "w-3.5 h-3.5")} />
  ) : status === "partial" ? (
    <AlertCircle className={cn("text-amber-500", compact ? "w-3 h-3" : "w-3.5 h-3.5")} />
  ) : null;

  const label = preparing
    ? "Preparing offline…"
    : status === "available"
    ? "Offline ready"
    : status === "partial"
    ? "Stale cache"
    : "Not yet cached";

  // Don't render "not yet cached" in compact mode to reduce noise
  if (compact && status === "not_available" && !preparing) return null;

  return (
    <span className={cn(
      "inline-flex items-center gap-1 font-medium",
      compact ? "text-xs" : "text-xs",
      preparing ? "text-muted-foreground"
        : status === "available" ? "text-emerald-600"
        : status === "partial" ? "text-amber-500"
        : "text-muted-foreground"
    )}>
      {icon}
      {label}
    </span>
  );
}
